import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { clearAppState } from "../utils/localStorage";

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_API_URL;
//const API_URL = "";
console.log("=== MINHA API URL ATUAL É ===", API_URL);

export function AuthProvider({ children }) {
  const watchIdRef = useRef(null);
  const [user, setUser] = useState(undefined);
  const [accessToken, setAccessToken] = useState(null);
  const accessTokenRef = useRef(accessToken);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    error: null,
  });

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const logout = async () => {
    await fetch(`${API_URL}/api/auth/logout/`, {
      method: "POST",
      credentials: "include",
    });

    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setUser(null);
    setAccessToken(null);
    setLocation({ latitude: null, longitude: null, error: null });
    localStorage.removeItem("kyc_verified");
    clearAppState();
  };

  const authFetch = useCallback(
    async (url, options = {}) => {
      const doRequest = (token) => {
        return fetch(url, {
          ...options,
          credentials: "include",
          headers: {
            ...options.headers,
            Authorization: `Bearer ${token}`,
          },
        });
      };

      let response = await doRequest(accessToken);

      if (response.ok) {
        return response;
      }

      const errorData = await response
        .clone()
        .json()
        .catch(() => ({}));
      const detail = String(errorData?.detail || "");

      const looksLikeTokenError = response.status === 401;

      if (!looksLikeTokenError) {
        return response;
      }

      const refreshRes = await fetch(`${API_URL}/api/auth/refresh/`, {
        method: "POST",
        credentials: "include",
        headers: {
          //"ngrok-skip-browser-warning": "true",
        },
      });

      if (!refreshRes.ok) {
        logout();
        throw new Error("Sessão expirada");
      }

      const refreshData = await refreshRes.json();

      if (!refreshData?.access) {
        logout();
        throw new Error("Sessão expirada");
      }

      const newToken = refreshData.access;
      setAccessToken(newToken);
      return await doRequest(newToken);
    },
    [accessToken],
  );

  const stopUserLocation = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      console.log(
        "Rastreamento de GPS desligado automaticamente (sem viagens ativas status 7).",
      );
    }
  }, []);

  const loadUser = async () => {
    try {
      const refreshRes = await fetch(`${API_URL}/api/auth/refresh/`, {
        method: "POST",
        credentials: "include",
        headers: {
          //"ngrok-skip-browser-warning": "true",
        },
      });

      if (!refreshRes.ok) {
        setUser(false);
        setAccessToken(null);
        return;
      }

      const refreshData = await refreshRes.json();

      if (!refreshData?.access) {
        setUser(false);
        setAccessToken(null);
        return;
      }

      setAccessToken(refreshData.access);

      const meRes = await fetch(`${API_URL}/api/auth/me/`, {
        method: "GET",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${refreshData.access}`,
        },
      });

      if (meRes.ok) {
        const data = await meRes.json();
        setUser(data);
        return data;
      } else {
        setUser(false);
        return null;
      }
    } catch {
      setUser(false);
      setAccessToken(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        accessToken,
        setAccessToken,
        loading,
        logout,
        authFetch,
        showLoginModal,
        setShowLoginModal,
        loadUser,
        location,
        stopUserLocation,
        accessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
